import { Utils } from '@sparrow-marketing/contracts/lib';
import { IDataSourceAccounts } from '../../views/datasources/datasources';

export type ActionFlow = 'upgrade' | 'downgrade' | 'none';
export type CURRENCY = '$';
export type UNLIMITED = 'unlimited';
export type PricingIcon =
  | 'pricing-free'
  | 'pricing-starter'
  | 'pricing-professional'
  | 'pricing-team';
export type PaymentPeriod = 'month' | 'year';
export type PlanType = 'Free' | 'Starter' | 'Professional' | 'Team';
export interface IPricing {
  integer: number;
  decimal: string;
}
export interface ICardExpiration {
  month?: string;
  year?: string;
}

export type REQUIREMENTS = {
  workspace: number | UNLIMITED;
  user: number | UNLIMITED;
  datasources: number | UNLIMITED;
};
export type PRIORITY = 1 | 2 | 3 | 4 | 5;
export interface IPlan {
  priority: number;
  icon: PricingIcon | string;
  plan: PlanType;
  priceId: string;
  isFreePlan?: boolean;
  currency: CURRENCY;
  price: IPricing;
  paymentPeriod: PaymentPeriod;
  requirements: REQUIREMENTS;
  isLegacy?: boolean;
}

export interface ICoupon {
  couponId: string;
  couponCode: string;
  couponDetail: string;
  amount: number;
  status: string;
  discountFormat: string;
  applicablePlans: string[];
}

export type CommonFeatures = 'available';
export type FeaturesName =
  | 'Data sources'
  | 'Workspaces'
  | 'Users'
  | 'Analytics Dashboards'
  | 'Google Add-on'
  | 'Reporting';

export type PlanDetails = {
  [key in PlanType]: [
    { 'Data sources': string },
    { Workspaces: string },
    { Users: string },
    { 'Analytics Dashboards': CommonFeatures },
    { 'Google Add-on': CommonFeatures },
    { Reporting: CommonFeatures }
  ];
};

export type STEPS = { [key: string]: string };
export interface ICreditCard {
  cardId: string;
  name: string;
  cardNumber: string;
  expirationDate: ICardExpiration;
  cvv: string;
  cardtype?: string;
}

export interface ISaveWorkspaceDowngrade {
  workspace: string[];
  datasources: string[];
}

/**
 *
 * This type should be moved to client specific file
 *
 */

export interface IClient {
  accounts: IDataSourceAccounts<any>[];
  businessId: number;
  disabled: any;
  email: string | null;
  logo: string | null;
  name: string;
  timezone: string;
}

export type CleintState = Utils.ApiModel<IClient>;

export const PRICING_PLAN: any[] = [
  {
    priority: 1,
    icon: 'pricing-free',
    plan: 'Free',
    currency: '$',
    monthlyPrice: { integer: 0, decimal: '00' },
    annualPrice: { integer: 0, decimal: '00' },
    paymentPeriod: 'month',
    requirements: { datasources: 1, user: 1, workspace: 1 },
  },
  {
    priority: 2,
    icon: 'pricing-starter',
    plan: 'Starter',
    currency: '$',
    monthlyPrice: { integer: 24, decimal: '99' },
    annualPrice: { integer: 19, decimal: '00' },
    paymentPeriod: 'month',
    requirements: { datasources: 5, user: 1, workspace: 2 },
  },
  {
    priority: 3,
    icon: 'pricing-professional',
    plan: 'Professional',
    currency: '$',
    monthlyPrice: { integer: 49, decimal: '00' },
    annualPrice: { integer: 39, decimal: '00' },
    paymentPeriod: 'month',
    requirements: { datasources: 10, user: 5, workspace: 5 },
  },
  {
    priority: 4,
    icon: 'pricing-team',
    plan: 'Team',
    currency: '$',
    monthlyPrice: { integer: 119, decimal: '00' },
    annualPrice: { integer: 99, decimal: '00' },
    paymentPeriod: 'month',
    requirements: {
      datasources: 'unlimited',
      user: 20,
      workspace: 20,
    },
  },
];

export const PLAN_DETAILS: Omit<PlanDetails, 'Legacy'> = {
  Free: [
    { 'Data sources': '1 Datasource' },
    { Workspaces: '1' },
    { Users: '1' },
    { 'Analytics Dashboards': 'available' },
    { 'Google Add-on': 'available' },
    { Reporting: 'available' },
  ],
  Starter: [
    { 'Data sources': '5 Datasources' },
    { Workspaces: '2' },
    { Users: '1' },
    { 'Analytics Dashboards': 'available' },
    { 'Google Add-on': 'available' },
    { Reporting: 'available' },
  ],
  Professional: [
    { 'Data sources': '10 Datasources' },
    { Workspaces: '5' },
    { Users: '5' },
    { 'Analytics Dashboards': 'available' },
    { 'Google Add-on': 'available' },
    { Reporting: 'available' },
  ],
  Team: [
    { 'Data sources': 'Unlimited Datasources' },
    { Workspaces: '20' },
    { Users: '20' },
    { 'Analytics Dashboards': 'available' },
    { 'Google Add-on': 'available' },
    { Reporting: 'available' },
  ],
};

export const STEPS = {
  SELECT_PLAN: 'Select Plan',
  ADD_PAYMENT_METHOD: 'Add Payment Method',
  UPDATE_PAYMENT_METHOD: 'Update payment method',
  ADD_CREDIT_CARD: 'Add credit card',
  SELECT_PAYMENT_METHOD: 'Select Payment Method',
  ENTER_PAYMENT_DETAILS: 'Enter Payment Details',
  FULFILL_PLAN_REQUIREMENTS: 'Fullfill plan requirements',
  TEAM_MEMBER_WARNING: 'Team member warning',
  CHECKOUT: 'Checkout',
  SUCCESS: 'SUCCESS',
};

// these are the sumo coupons
export const PARTNER_COUPONS: Utils.DynamicObject<string, boolean> = {
  ao74ou9Xc27z: true,
  S4Ww6shUCPiD: true,
};

export const Currency = {
  getCurrencySymbol: (currency: string) => {
    if (!currency) return '';

    return currency.match(/[^0-9.-]+/)?.[0] || '';
  },
  getCurrencyAmount: (currency: string) => {
    if (!currency) return '';

    return parseFloat(currency.replace(/[^0-9.-]+/g, ''));
  },
  formatCurrency({ symbol = '$', amount }: { symbol: string; amount: number }) {
    return `${symbol}${amount.toFixed(2)}`;
  },
};
